import { useState, useEffect, useRef } from 'react'
import { ApolloClient, InMemoryCache } from '@apollo/client'
import link from './link'
import { persistCache, CachePersistor } from 'apollo3-cache-persist'
import { apolloStorage } from 'config/storage'
import { fetchAuthSession } from 'aws-amplify/auth'
import { Cache } from 'aws-amplify/utils'
import { version } from '../../../package.json'

const useApolloClient = () => {
	const apolloClient = useRef()
	const cachePersistor = useRef()
	const [providerReady, setProviderReady] = useState(false)
	const [companyRefreshed, setCompanyRefreshed] = useState(false)

	const resetCache = async () => {
		if (cachePersistor.current) {
			cachePersistor.current.pause() // Pause automatic persistence.
			await cachePersistor.current.purge() // Delete everything in the storage provider.
		}
		if (apolloClient.current) {
			await apolloClient.current.clearStore()	//clear apollo-client store
		}
		await Cache.clear()	//clears aws-amplify cache
	}

	useEffect(() => {
		const shouldPersistCache = !JSON.parse(sessionStorage.getItem('useSessionStorage'))
		const cache = new InMemoryCache({
			typePolicies: {
				AppConfig: { keyFields: [] } // singleton — one AppConfig per cache
			}
		})
		const clientParams = {
			link,
			version,
			cache: cache,
			connectToDevTools: true,
			name: `cashassist-web${process.env.NODE_ENV === 'production' ? '' : '-dev'}`
		}
		if (shouldPersistCache) {
			const persistantCacheParams = {
				cache: cache,
				maxSize: false,
				storage: apolloStorage
			}
			cachePersistor.current = new CachePersistor(persistantCacheParams)
			persistCache(persistantCacheParams).then(() => {
				apolloClient.current = new ApolloClient(clientParams)
				setProviderReady(true)
			})
		} else {
			apolloClient.current = new ApolloClient(clientParams)
			// pre-existing pattern, symmetric with the persistCache().then() branch above
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setProviderReady(true)
		}
	}, [])

	useEffect(() => {
		const refreshCompany = async () => {
			try {
				const before = await fetchAuthSession()
				const payload = before.tokens?.idToken?.payload || {}
				const accessRights = JSON.parse(payload['custom:access_rights'] || '{}')
				if (accessRights.chc_sa) {
					/* super-admins can switch company in the console — a stale token would
					   keep serving the previous company's cached data */
					const after = await fetchAuthSession({ forceRefresh: true })
					const newCompany = after.tokens?.idToken?.payload?.['custom:company']
					if (!newCompany || payload['custom:company'] !== newCompany) {
						await resetCache()
					}
				}
			} catch (error) {
				/* forceRefresh rejecting (flaky network) must not leave a stale
				   company's cache silently served — reset defensively */
				console.error('refreshCompany failed, resetting cache defensively', error)
				try {
					await resetCache()
				} catch (resetError) {
					console.error('resetCache failed after refreshCompany error', resetError)
				}
			} finally {
				setCompanyRefreshed(true)
			}
		}
		refreshCompany()
	}, [])

	// pre-existing pattern: exposes the latest ref values to consumers on each render
	/* eslint-disable react-hooks/refs */
	return {
		client: apolloClient.current,
		cachePersistor: cachePersistor.current,
		isLoading: !(providerReady && companyRefreshed),
		resetCache
	}
	/* eslint-enable react-hooks/refs */
}
export default useApolloClient
